import { DropdownBase } from '@wix/design-system';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ZIndexContext } from './../../ricos-context';
import { UiComponentsProvider } from './../../ui-components/ui-components-provider';

import styles from './emoji-suggestions-modal-scss';

export type EmojiSuggestionsModalProps = {
  command: (params: { emoji: string; query: string }) => void;
  items: EmojiWithShortcode[];
  query: string;
  text: string;
  messages: {
    dispatch: (msg: Message) => void;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    subscribe: (cb: Function) => void;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    unsubscribe: (cb: Function) => void;
  };
};

type Message =
  | {
      type: 'onKeyDown';
      payload: {
        event: KeyboardEvent;
      };
    }
  | {
      type: 'onChange';
      payload: {
        items: EmojiWithShortcode[];
        text: string;
      };
    };

type EmojiWithShortcode = {
  id: string;
  name: string;
  keyword: string[];
  native: string;
  shortcodes: string;
  unified: string;
};

export const EmojiSuggestionsModal: React.FC<EmojiSuggestionsModalProps> = ({
  command,
  items,
  query,
  messages,
}) => {
  const zIndexService = useContext(ZIndexContext);

  const currElement: React.MutableRefObject<null | DropdownBase> = useRef(null);
  const [refElement, setRefElement] = useState<HTMLDivElement | null>(null);

  const [selectedId, setSelectedId] = useState<string>();

  useEffect(() => {
    setSelectedId(items[0]?.id);
  }, [items]);

  useEffect(() => {
    const handleMessage = ({ type, payload }: Message) => {
      if (type === 'onKeyDown') {
        const { event } = payload;
        if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
          const direction = event.key === 'ArrowUp' ? -1 : 1;
          if (!items.length) {
            return true;
          }
          setSelectedId(prev => {
            const currIndex = items.findIndex(option => option.id === prev);
            const nextIndex = (currIndex + direction + items.length) % items.length;
            return items[nextIndex].id;
          });
          return true;
        }

        if (event.key === 'Enter') {
          const option = items.find(option => option.id === selectedId);

          if (!option) {
            return false;
          }

          command({ emoji: option.native, query });
          return true;
        }
      }
    };

    messages?.subscribe(handleMessage);
    return () => messages?.unsubscribe(handleMessage);
  }, [messages, items, query, command, selectedId]);

  const dropdownOptions = useMemo(
    () =>
      items.map((emoji: EmojiWithShortcode) => ({
        as: 'button',
        ellipsis: false,
        id: emoji.id,
        onClick: () => {
          command({ emoji: emoji.native, query });
        },
        optionTitle: `${emoji.native} ${emoji.shortcodes}`,
        shouldFocusWithoutScroll: true,
        title: false,
        value: 'action',
      })),
    [items, query, command]
  );

  return (
    <div
      className={styles.emojiSuggestionsModal}
      style={{ zIndex: zIndexService.getZIndex('DIALOG') }}
      ref={setRefElement}
    >
      {refElement && (
        <UiComponentsProvider containerEl={refElement}>
          <DropdownBase
            ref={currElement}
            placement="bottom-start"
            minWidth={280}
            maxHeight={250}
            focusOnOption={selectedId}
            options={dropdownOptions}
            open
            listType="action"
          />
        </UiComponentsProvider>
      )}
    </div>
  );
};
